<script>

import FrequentlyAskedQuestions from '@rei/frequently-asked-questions';
import BrandRail from '@rei/brand-rail';
import { StoriesComponent } from '@rei/landing-story';
import { LandingContentBlock } from '@rei/landing-content-block';
import GreenlightLead from './components/GreenlightLead.vue';
import BrandBanner from './components/BrandBanner.vue';

import analytics from '../../common/mixins/analytics';
import {
  getContentBlockProps,
  getImageComponentProps,
  getStoryComponentProps,
  getBrandRailProps,
} from '../../utils/getPropsFromData';

export default {
  name: 'GreenlightLanding',
  components: {
    GreenlightLead,
    FrequentlyAskedQuestions,
    BrandRail,
    LandingContentBlock,
    StoriesComponent,
    BrandBanner,
  },
  mixins: [analytics],
  props: {
    additionalData: { type: Object, required: true, default: () => {} },
    componentModels: { type: Object, required: true, default: () => {} },
  },
  computed: {
    renderingMap() {
      return {
        lead: !!this.leadProps,
        'featured-story': !!this.featuredStoryProps,
        'application-steps': !!this.applicationStepsProps,
        'looking-for': !!this.lookingForProps,
        'featured-brands': !!this.featuredBrandsProps,
        'featured-story-b': !!this.featuredStoryBProps,
        'brand-banner': !!this.brandBannerProps,
        faq: !!this.frequentlyAskedQuestions,
      };
    },
    leadProps() {
      const { lead } = this.componentModels || {};
      const content = getContentBlockProps(lead);
      const media = getImageComponentProps(lead);

      return (lead && {
        content,
        media,
      }) || null;
    },
    featuredStoryProps() {
      const { featuredStoryA } = this.componentModels || {};
      const renderComponent = !!featuredStoryA;
      return (renderComponent
      && { ...getStoryComponentProps(featuredStoryA) }
      ) || null;
    },
    applicationStepsProps() {
      const { applicationSteps } = this.componentModels || {};
      const renderComponent = !!applicationSteps;
      return (renderComponent
        && {
          headingSection: { text: applicationSteps?.content?.heading?.text },
          ...getStoryComponentProps(applicationSteps),
        }
      ) ?? null;
    },
    lookingForProps() {
      const { contentBlock } = this.componentModels || {};
      const content = getContentBlockProps(contentBlock);
      return ((!!contentBlock) && {
        content,
      });
    },
    featuredBrandsProps() {
      const { brandRail } = this.componentModels || {};
      const brandRailProps = getBrandRailProps(brandRail, { grid: 4 });
      const renderComponent = brandRail;
      return (renderComponent && {
        heading: brandRail?.content?.heading?.text,
        brandRailProps,
      });
    },
    frequentlyAskedQuestions() {
      const { frequentlyAskedQuestions } = this.componentModels || {};
      return frequentlyAskedQuestions;
    },

    featuredStoryBProps() {
      const { featuredStoryB } = this.componentModels || {};
      const renderComponent = !!featuredStoryB;
      return (renderComponent
      && { ...getStoryComponentProps(featuredStoryB) }
      ) || null;
    },
    brandBannerProps() {
      const { banner } = this.componentModels || {};

      return (banner && {
        author: banner?.content?.body,
        quote: banner?.content?.heading?.text,
        'background-image': getImageComponentProps(banner),
        'brand-logo': {
          xs: banner.media?.['logo-xs']?.src,
          alt: '',
        },
      }) || null;
    },
  },
};
</script>
<template>
  <div>
    <section
      v-if="renderingMap['lead']"
      :data-location-name="analyticsMap['lead']"
      data-ui="greenlight-lead"
      class="greenlight__container greenlight__container--no-padding"
    >
      <greenlight-lead v-bind="leadProps" />
    </section>
    <section
      v-if="renderingMap['featured-story']"
      :data-location-name="analyticsMap['featured-story']"
      data-ui="greenlight-featured-story"
      class="greenlight__container greenlight__container--narrow"
    >
      <stories-component v-bind="featuredStoryProps" />
    </section>
    <section
      v-if="renderingMap['application-steps']"
      :data-location-name="analyticsMap['application-steps']"
      data-ui="greenlight-application-steps"
      class="greenlight__container greenlight__container--narrow"
    >
      <stories-component v-bind="applicationStepsProps" />
    </section>
    <section
      v-if="renderingMap['looking-for']"
      :data-location-name="analyticsMap['looking-for']"
      data-ui="greenlight-looking-for"
      class="greenlight__container
        greenlight__container--narrow
        greenlight__looking
        greenlight__looking--centered"
    >
      <landing-content-block
        v-bind="lookingForProps.content"
      />
    </section>
    <section
      v-if="renderingMap['featured-brands']"
      :data-location-name="analyticsMap['featured-brands']"
      data-ui="greenlight-featured-brands"
      class="greenlight__container greenlight__container--narrow"
    >
      <h2
        v-if="featuredBrandsProps.heading"
        class="greenlight__heading greenlight__heading--centered"
        v-html="featuredBrandsProps.heading"
      />
      <brand-rail
        v-bind="featuredBrandsProps.brandRailProps"
      />
    </section>
    <section
      v-if="renderingMap['featured-story-b']"
      :data-location-name="analyticsMap['featured-story-b']"
      data-ui="greenlight-featured-story-b"
      class="greenlight__container greenlight__container--narrow"
    >
      <stories-component v-bind="featuredStoryBProps" />
    </section>
    <section
      v-if="renderingMap['brand-banner']"
      :data-location-name="analyticsMap['brand-banner']"
      data-ui="greenlight-brand-banner"
      class="greenlight__container greenlight__container--no-pb"
    >
      <brand-banner
        v-bind="brandBannerProps"
      />
    </section>
    <section
      v-if="renderingMap['faq']"
      :data-location-name="analyticsMap['faq']"
      data-ui="greenlight-faq"
      class="greenlight__container greenlight__container--no-padding"
    >
      <frequently-asked-questions
        id="greenlight-faq"
        :faq="frequentlyAskedQuestions"
        background-color="secondary"
      />
    </section>
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.greenlight {
  &__container {
    padding-top: $cdr-space-one-and-a-half-x;
    padding-bottom: $cdr-space-one-and-a-half-x;

    @include cdr-sm-mq-up {
      padding-top: $cdr-space-three-x;
      padding-bottom: $cdr-space-three-x;
    }

    &--narrow {
      @include cdr-container;
    }

    &--no-padding {
      padding: 0;
    }
    &--no-pb {
      padding-bottom: 0;
    }

  }

  &__looking {
    &--centered {
      @include cdr-sm-mq-up {
        text-align: center;
      }
    }
  }

  &__looking-image {
    padding-bottom: $cdr-space-two-x;
    border-radius: 0.8rem;
  }

  &__heading {
    @include cdr-text-heading-serif-600;
    margin: 0 0 $cdr-space-one-x;

    @include cdr-sm-mq-up {
      @include cdr-text-heading-serif-700;
    }

    &--centered {
      @include cdr-sm-mq-up {
        text-align: center;
      }
    }
  }

  &__text {
    margin: 0 0 $cdr-space-two-x;

    &--centered {
      @include cdr-sm-mq-up {
        text-align: center;
      }
    }
  }
}
</style>
